// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Minimal DOM helpers. No framework — the UI is small enough that a `h()` and a
 * couple of utilities beat a dependency (spec §4: near-zero runtime deps).
 *
 * There is deliberately no `innerHTML` path anywhere in this module. Almost
 * every string the UI displays comes out of a board file — net names, reference
 * designators, silkscreen text, library ids — and a `.kicad_pcb` is not a
 * trusted document just because it arrived by file transfer. Everything goes in
 * as a text node, so markup in a board file renders as the characters it is.
 */

type Child = Node | SVGElement | string | number | null | undefined | false;

export interface Attrs {
  class?: string;
  id?: string;
  title?: string;
  type?: string;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  checked?: boolean;
  name?: string;
  step?: string;
  accept?: string;
  rows?: string;
  onclick?: (ev: MouseEvent) => void;
  onchange?: (ev: Event) => void;
  oninput?: (ev: Event) => void;
  ondblclick?: (ev: MouseEvent) => void;
  [key: string]: unknown;
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2), value as EventListener);
    } else if (key === 'checked' || key === 'disabled') {
      (el as unknown as Record<string, unknown>)[key] = Boolean(value);
    } else if (key === 'class') {
      el.className = String(value);
    } else {
      el.setAttribute(key, String(value));
    }
  }
  append(el, children);
  return el;
}

export function append(parent: HTMLElement, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.append(typeof child === 'string' || typeof child === 'number' ? String(child) : child);
  }
}

/**
 * A small stroked icon.
 *
 * Inline SVG rather than a glyph font or a unicode symbol: the symbols for
 * "fit to view" and "chevron" render inconsistently across platforms, and this
 * has no external dependency and inherits `currentColor`.
 */
export function svgIcon(path: string, size = 12): SVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.7');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  const d = document.createElementNS(NS, 'path');
  d.setAttribute('d', path);
  svg.append(d);
  return svg;
}

/**
 * Milliseconds to wait for a paint before proceeding regardless.
 *
 * Long enough for a few frames at 60 Hz, short enough to be imperceptible.
 */
const PAINT_TIMEOUT_MS = 60;

/**
 * Resolve once the browser has painted — or shortly afterwards regardless.
 *
 * One `requestAnimationFrame` only guarantees a callback *before* the next
 * paint, so a second nested one is what actually confirms a frame reached the
 * screen. But nested frames are not guaranteed to arrive at all: a background
 * tab throttles them to nothing, and a headless renderer never fires the second
 * one. Waiting on them alone therefore hangs forever — the work never starts,
 * and the progress overlay stays up for good.
 *
 * So the frame callbacks race a timeout, and whichever comes first wins. The
 * worst case is a paint that has not landed yet; the alternative was a UI that
 * locks up the moment the user switches tabs.
 */
export function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      resolve();
    };

    setTimeout(finish, PAINT_TIMEOUT_MS);

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTimeout(finish, 0));
      });
    } else {
      setTimeout(finish, 0);
    }
  });
}

export function clear(el: HTMLElement): HTMLElement {
  el.replaceChildren();
  return el;
}

/** Number formatting for coordinates: fixed but without trailing zero noise. */
export function mm(v: number, digits = 3): string {
  const s = v.toFixed(digits);
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Save a file. Uses the File System Access API when available so the user can
 * write straight back over their project, and degrades to a Blob download
 * everywhere else (spec §4).
 */
export async function saveFile(fileName: string, text: string): Promise<'saved' | 'downloaded'> {
  const picker = (globalThis as { showSaveFilePicker?: unknown }).showSaveFilePicker;
  if (typeof picker === 'function') {
    try {
      const handle = await (picker as (opts: unknown) => Promise<FileSystemFileHandle>)({
        suggestedName: fileName,
        types: [{ description: 'KiCad PCB', accept: { 'application/octet-stream': ['.kicad_pcb'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(text);
      await writable.close();
      return 'saved';
    } catch (err) {
      // AbortError means the user cancelled: respect that, do not fall back to
      // silently dumping the file into their downloads folder.
      if ((err as DOMException)?.name === 'AbortError') throw err;
    }
  }
  const url = URL.createObjectURL(new Blob([text], { type: 'application/octet-stream' }));
  const a = h('a', { href: url, download: fileName });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
}

/** Read a dropped or picked file as text. */
export function readFile(file: File): Promise<string> {
  return file.text();
}

/** Wire an element up as both a click-to-pick and a drag-and-drop target. */
export function makeDropTarget(
  el: HTMLElement,
  accept: string,
  onFile: (file: File) => void,
): void {
  const input = h('input', { type: 'file', accept, class: 'hidden' });
  input.style.display = 'none';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) onFile(file);
    input.value = '';
  });
  el.append(input);

  el.addEventListener('click', (ev) => {
    if ((ev.target as HTMLElement).tagName !== 'INPUT') input.click();
  });
  el.addEventListener('dragover', (ev) => {
    ev.preventDefault();
    el.classList.add('over');
  });
  el.addEventListener('dragleave', () => el.classList.remove('over'));
  el.addEventListener('drop', (ev) => {
    ev.preventDefault();
    el.classList.remove('over');
    const file = ev.dataTransfer?.files?.[0];
    if (file) onFile(file);
  });
}


/**
 * Where the column must scroll to bring a section into view.
 *
 * Section headers are pinned in a stack, so "into view" is not the top of the
 * column — it is just below the headers that stack above this one. Scrolling to
 * the raw top would slide the section straight under them.
 *
 * `scrollIntoView` cannot do this job at all here: the element that groups a
 * section generates no box (`display: contents`, so its header can pin to the
 * column rather than to the section), and an element with no box cannot be
 * scrolled to.
 *
 * Returned as arithmetic so it can be checked without a layout engine.
 */
export function sectionScrollTarget(options: {
  /** The column's current scroll offset. */
  scrollTop: number;
  /** Top of the scrolling column, in viewport coordinates. */
  columnTop: number;
  /** Top of the section's body, in viewport coordinates. */
  contentTop: number;
  /** How many headers stack above this one. */
  headersAbove: number;
  headerHeight: number;
}): number {
  const { scrollTop, columnTop, contentTop, headersAbove, headerHeight } = options;
  const reserved = (headersAbove + 1) * headerHeight;
  return Math.max(0, scrollTop + contentTop - columnTop - reserved);
}
